/**
 * pages/Statements.tsx — monthly statement archive.
 *
 * Design contract:
 *
 *   Statement download is READ-ONLY. A statement is a GET that returns
 *   the user's own data — moves no money, changes no auth state.
 *   Therefore it does NOT open the mobile-approval modal. Approval
 *   gates money movement and auth changes. Reading your own data is
 *   neither.
 *
 *   Reg E requires the periodic (monthly) statement to be
 *   downloadable. CSV is available today; PDF is on the roadmap.
 *   The screen renders CSV as the ONLY download per-row and states
 *   "PDF is coming" once, at the top of the page — a disabled button
 *   or a per-row "PDF soon" chip still reads as a broken feature; a
 *   product that doesn't have PDFs is honest about not having them.
 *
 *   The backend PDF endpoint continues to return a structured 501
 *   Not Implemented; the api client turns that into an ApiClientError
 *   with code "not_yet_implemented" so a bookmarked URL fails cleanly
 *   instead of surfacing a raw status code. This screen just doesn't
 *   put such a URL in front of anyone.
 *
 *   Empty state — an account with no completed months (the common
 *   case at launch) sees the plain "no statements yet" panel, not an
 *   error. Statements are generated at the close of each calendar
 *   month.
 *
 *   The custom-range card is an ACTIVITY EXPORT, not a periodic
 *   statement. It hits POST /v1/activity/export and downloads a CSV
 *   for the requested span (up to 24 months). It's a convenience
 *   download, not the Reg E deliverable.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Field,
  Money,
  useToast,
} from "../components";
import { api, ApiClientError, API_BASE } from "../api/client";
import { AppShell } from "./AppShell";
import styles from "./Statements.module.css";

// ---------------------------------------------------------------------------
// Wire types — mirror the backend response shape from routes/statements.py.
// Kept local because these are strictly server → client and we don't
// need cross-module reuse.
// ---------------------------------------------------------------------------

interface MoneyEnvelope {
  amount: string;
  asset: string;
}

interface StatementRow {
  period: string;             // "2026-07"
  year: number;
  month: number;
  month_label: string;        // "July 2026"
  period_start: string;
  period_end: string;
  generated_at: string;
  tx_count: number;
  total_volume: MoneyEnvelope;
  opening_balance: MoneyEnvelope;
  closing_balance: MoneyEnvelope;
  formats: ("csv" | "pdf")[];
  download_url: { csv: string; pdf: string };
}

interface StatementListResponse {
  data: StatementRow[];
  empty: boolean;
}

// ---------------------------------------------------------------------------
// Custom range downloader — POST /v1/activity/export
//
// The response is a CSV byte stream with Content-Disposition, so we
// can't use a plain <a href>. We fetch it manually, capture the Blob,
// and trigger a browser download via a temporary <a>. Standard pattern.
// ---------------------------------------------------------------------------

async function downloadActivityExport(
  from: string, to: string,
): Promise<void> {
  const url = `${API_BASE}/v1/activity/export`;
  const csrf = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("aster_csrf="))
    ?.slice("aster_csrf=".length);

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/csv",
      ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ from, to }),
  });

  if (!res.ok) {
    // Try to surface the structured error envelope. If the response
    // isn't JSON (a 500 might not be), fall back to text.
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error?.message || msg;
    } catch {
      msg = (await res.text()).slice(0, 300) || msg;
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const filename = `aster-activity-${from}_to_${to}.csv`;
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Statements() {
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeBusy, setRangeBusy] = useState(false);
  const [rows, setRows] = useState<StatementRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const toast = useToast();

  // Fetch on mount. React strict-mode double-invocation is fine here —
  // the endpoint is a pure read and returns the same body twice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api<StatementListResponse>("/v1/statements?months=24");
        if (!cancelled) setRows(resp.data);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiClientError
            ? e.message
            : e instanceof Error
              ? e.message
              : "unknown error";
        setLoadError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isLoading = rows === null && loadError === null;
  const isEmpty = rows !== null && rows.length === 0;

  const onCustomRange = useCallback(async () => {
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return;
    setRangeBusy(true);
    try {
      await downloadActivityExport(rangeStart, rangeEnd);
      toast.push({
        intent: "completed",
        title: "Activity CSV downloaded",
        body: `Range ${rangeStart} to ${rangeEnd}`,
      });
    } catch (e) {
      toast.push({
        intent: "failed",
        title: "Export failed",
        body: e instanceof Error ? e.message : "Try again in a moment.",
      });
    } finally {
      setRangeBusy(false);
    }
  }, [rangeStart, rangeEnd, toast]);

  return (
    <AppShell current="statements">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Statements</p>
          <h1 className={styles.pageTitle}>Monthly summaries and downloads</h1>
        </div>
      </header>

      <p className={styles.leadCopy}>
        Statements download as CSV. PDF is coming.
      </p>

      {/* Custom activity export */}
      <Card className={styles.rangeCard}>
        <div className={styles.rangeCardInner}>
          <div>
            <p className={styles.rangeTitle}>Custom date range</p>
            <p className={styles.rangeBody}>
              Export a CSV of every transaction in a range up to 24 months.
              This is a convenience export — the monthly statement below is
              the Reg-E-required periodic statement.
            </p>
          </div>
          <div className={styles.rangeFields}>
            <Field
              label="From"
              type="date"
              value={rangeStart}
              onChange={setRangeStart}
            />
            <Field
              label="To"
              type="date"
              value={rangeEnd}
              onChange={setRangeEnd}
            />
            <Button
              variant="primary"
              onClick={onCustomRange}
              disabled={
                !rangeStart ||
                !rangeEnd ||
                rangeStart > rangeEnd ||
                rangeBusy
              }
            >
              {rangeBusy ? "Preparing…" : "Download CSV"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Monthly list */}
      <section className={styles.monthList}>
        {isLoading && (
          <div className={styles.monthRow}>
            <p className={styles.monthMeta}>Loading…</p>
          </div>
        )}

        {loadError && (
          <Card className={styles.honestBanner}>
            <p className={styles.honestBannerTitle}>Could not load statements</p>
            <p className={styles.honestBannerBody}>
              {loadError} — refresh in a moment. If it persists, email{" "}
              <a href="mailto:support@astercash.com" className={styles.mailLink}>
                support@astercash.com
              </a>
              .
            </p>
          </Card>
        )}

        {isEmpty && (
          <Card className={styles.honestBanner}>
            <p className={styles.honestBannerTitle}>No statements yet</p>
            <p className={styles.honestBannerBody}>
              Statements are generated at the close of each calendar month.
              Once your first month closes it will appear here — usually
              within one business day of month-end.
            </p>
          </Card>
        )}

        {rows?.map((r) => (
          <div key={r.period} className={styles.monthRow}>
            <div className={styles.monthLeft}>
              <p className={styles.monthLabel}>{r.month_label}</p>
              <p className={styles.monthMeta}>
                {r.tx_count === 0
                  ? `No activity · closing `
                  : `${r.tx_count} transaction${r.tx_count === 1 ? "" : "s"} · closing `}
                <Money
                  value={BigInt(r.closing_balance.amount)}
                  asset={r.closing_balance.asset}
                  size="body"
                  intent="muted"
                />
              </p>
            </div>
            <div className={styles.monthActions}>
              {/*
                CSV is the only rendered download. PDF is not shown as
                a disabled button or a "coming soon" chip — an inert
                affordance still reads as a broken feature. The backend
                endpoint still exists (returns a structured 501 via
                the api client) so a bookmarked URL fails cleanly; we
                just don't put a bookmark in front of anyone.
              */}
              {r.formats.includes("csv") ? (
                <a
                  href={r.download_url.csv}
                  download={`aster-statement-${r.year}-${String(r.month).padStart(2, "0")}.csv`}
                  className={styles.dlLink}
                  data-testid={`dl-${r.period}-csv`}
                >
                  CSV
                </a>
              ) : (
                <span className={styles.pendingTag}>CSV soon</span>
              )}
            </div>
          </div>
        ))}
      </section>
    </AppShell>
  );
}
